/**
 * Type playbooks (free version)
 *
 * Goal: make results operational (behaviors + scripts), not just descriptive.
 * Keys must match `founderTypes` IDs in `src/data/founderTypes.js`.
 */

export const typePlaybooks = {
  visionary: {
    stressFailureMode:
      'You jump to a new direction too fast. Under pressure, you “out-vision” the problem instead of narrowing to the smallest next step.',
    threeStepWeekPlan: [
      {
        title: 'Pick one “most important bet” and freeze it for 7 days',
        steps: [
          'Write the 1-sentence bet (who + problem + outcome).',
          'List 3 “nice-to-haves” you will not do this week.',
          'Share the bet with your team and ask for one risk you’re missing.',
        ],
      },
      {
        title: 'Convert vision into a 48-hour proof',
        steps: [
          'Define the fastest proof (a call, a landing page, a prototype).',
          'Set a 48-hour deadline and ship the proof.',
          'Decide: double down, iterate, or kill based on what you learned.',
        ],
      },
      {
        title: 'Install a “finish line” ritual',
        steps: [
          'End each day with: 1 win, 1 lesson, 1 next action.',
          'Pick tomorrow’s first task before you close your laptop.',
          'Protect 90 minutes of deep work before meetings.',
        ],
      },
    ],
    decisionChecklist: [
      'What is the smallest decision I can make today?',
      'What evidence would change my mind?',
      'What will we stop doing if we do this?',
      'Who is accountable for the next step (name + date)?',
    ],
    meetingScripts: [
      {
        title: 'Alignment opener (2 minutes)',
        script:
          '“Here’s the bet for this week. The outcome we want is ____. The risk is ____. By Friday, success looks like ____.”',
      },
      {
        title: 'De-scope phrase',
        script: '“That’s a great idea. Let’s park it. What’s the smallest next step for the current bet?”',
      },
    ],
  },
  executor: {
    stressFailureMode:
      'You over-index on throughput. Under pressure, you ship a lot… but risk shipping the wrong thing because “why now?” gets skipped.',
    threeStepWeekPlan: [
      {
        title: 'Re-anchor on the “why” before the “how”',
        steps: [
          'Write the customer promise in one sentence.',
          'Confirm the top constraint (time, money, quality, scope).',
          'Say no to 1 task that doesn’t move the constraint.',
        ],
      },
      {
        title: 'Build a single scoreboard',
        steps: [
          'Pick 1 metric that proves progress this week.',
          'Track it daily (5 minutes).',
          'If metric doesn’t move by day 3, change approach (not effort).',
        ],
      },
      {
        title: 'Create “decision-ready” options',
        steps: [
          'Bring 2 options, not 10.',
          'State tradeoffs (time/cost/risk).',
          'Recommend one option and commit to a date.',
        ],
      },
    ],
    decisionChecklist: [
      'What outcome matters (not the task)?',
      'What’s the cheapest test to validate we’re building the right thing?',
      'What’s the “good enough” definition for this week?',
      'What do we stop to make room?',
    ],
    meetingScripts: [
      {
        title: 'Scope control',
        script: '“If we add that, what do we remove? We can only pick two: scope, speed, polish.”',
      },
      {
        title: 'Commitment close',
        script: '“Owner is ____. First deliverable by ____. We’ll review results on __.”',
      },
    ],
  },
  innovator: {
    stressFailureMode:
      'You explode the solution space. Under pressure, you generate more options than the team can execute, and focus slips.',
    threeStepWeekPlan: [
      {
        title: 'Run one experiment end-to-end',
        steps: [
          'Pick ONE experiment with a clear pass/fail.',
          'Define success criteria in advance.',
          'Ship it, measure, and write the learning in one paragraph.',
        ],
      },
      {
        title: 'Constrain creativity on purpose',
        steps: [
          'Set a constraint (timebox, budget, or feature cap).',
          'Generate 10 ideas inside the constraint.',
          'Choose the simplest viable option.',
        ],
      },
      {
        title: 'Add a “shipping partner”',
        steps: [
          'Pair with an executor for weekly delivery.',
          'Agree on “done” and a Friday demo.',
          'Let the partner enforce scope; you protect novelty.',
        ],
      },
    ],
    decisionChecklist: [
      'Is this idea a feature, a product, or a distraction?',
      'What’s the smallest experiment that proves value?',
      'What is the simplest version that still feels “new”?',
      'What will we learn in 7 days?',
    ],
    meetingScripts: [
      {
        title: 'Idea parking lot',
        script: '“Let’s capture that in the parking lot. For this sprint, we’re only doing what proves ____.”',
      },
    ],
  },
  analyst: {
    stressFailureMode:
      'You delay commitment. Under pressure, you keep gathering data to feel safe, which can stall momentum and team confidence.',
    threeStepWeekPlan: [
      {
        title: 'Timebox analysis and decide',
        steps: [
          'Set a decision deadline (same day if possible).',
          'Gather only the top 3 inputs.',
          'Choose and document the reason + what would falsify it.',
        ],
      },
      {
        title: 'Turn insights into actions',
        steps: [
          'For every insight, assign one action.',
          'Pick the top action that moves the primary metric.',
          'Review outcomes weekly; update the model.',
        ],
      },
      {
        title: 'Talk to customers (fast evidence)',
        steps: [
          'Schedule 3 calls this week.',
          'Ask: “What did you try? What was hard? What did you expect?”',
          'Summarize patterns, not anecdotes.',
        ],
      },
    ],
    decisionChecklist: [
      'What’s the decision? (write it as a binary)',
      'What’s the cost of waiting 2 weeks?',
      'What’s the smallest test to reduce risk?',
      'What is “good enough” evidence?',
    ],
    meetingScripts: [
      {
        title: 'Decision framing',
        script: '“We’re deciding between A and B. Here are the top 3 risks. My recommendation is ____ because ____.”',
      },
    ],
  },
  strategist: {
    stressFailureMode:
      'You over-model the game. Under pressure, you plan and compare rather than shipping, and you can miss fast-moving opportunities.',
    threeStepWeekPlan: [
      {
        title: 'Pick one wedge and commit',
        steps: [
          'Define your wedge user and use-case.',
          'Write the 1-sentence positioning.',
          'Ship something that makes the wedge real (not a deck).',
        ],
      },
      {
        title: 'Replace planning with cadence',
        steps: [
          'Set a weekly ship date (Friday).',
          'Run a 20-minute weekly review: ship, learn, adjust.',
          'Keep strategy docs to 1 page max.',
        ],
      },
      {
        title: 'Win via clarity',
        steps: [
          'Kill one ambiguous initiative.',
          'Define success metrics for the remaining one.',
          'Share the plan with the team in 5 bullets.',
        ],
      },
    ],
    decisionChecklist: [
      'What is the narrowest segment we can win first?',
      'What would make this strategy obviously wrong?',
      'What is the simplest move that increases option value?',
      'What’s the fastest path to customer pull?',
    ],
    meetingScripts: [
      {
        title: 'Positioning clarity',
        script: '“For [ICP], we are [category] that [promise], unlike [alternative] because [reason].”',
      },
    ],
  },
  relationshipBuilder: {
    stressFailureMode:
      'You optimize for harmony over truth. Under pressure, you avoid hard conversations, which delays decisions and creates hidden misalignment.',
    threeStepWeekPlan: [
      {
        title: 'Schedule one hard conversation',
        steps: [
          'Pick the highest-stakes, most-avoided topic.',
          'Write: “I noticed ____. I’m worried ____. Can we align on ____?”',
          'End with a clear decision + next step.',
        ],
      },
      {
        title: 'Make expectations explicit',
        steps: [
          'Define roles (who decides, who executes).',
          'Agree on response times and meeting hygiene.',
          'Document the working agreement (1 page).',
        ],
      },
      {
        title: 'Build customer relationships on purpose',
        steps: [
          'Do 5 warm outreaches this week.',
          'Ask for referrals after value is delivered.',
          'Track relationships like a pipeline (simple).',
        ],
      },
    ],
    decisionChecklist: [
      'What truth am I avoiding saying?',
      'What decision would reduce tension immediately?',
      'What expectation needs to be explicit?',
      'What is the smallest boundary I can set today?',
    ],
    meetingScripts: [
      {
        title: 'Direct but kind',
        script: '“I want to be direct because I care about us winning. The issue is ____. The impact is ____. Can we agree on ____?”',
      },
    ],
  },
};

